import {
  Baby,
  ClipboardList,
  FileText,
  MessageSquareHeart,
  Paperclip,
  Receipt,
  Stethoscope,
  type LucideIcon,
} from 'lucide-react';

/**
 * Registre des sections du formulaire de consultation.
 *
 * La V1 dépliait ce registre en **24 blocs de route** (8 étapes × 3 modes) plus 3 redirections.
 * Ici, une seule route paramétrique le lit : ajouter une section, c'est ajouter une ligne.
 *
 * Le motif, l'anamnèse, les mesures de la séance et le schéma corporel forment une seule étape
 * (`motif`), dans cet ordre. Les mesures ne comptent pas comme une étape du compteur.
 * Les autres sections gardent leur ordre relatif. Les anciennes adresses `anamnese` et
 * `schema-corporel` ne sont plus des étapes : une section inconnue renvoie au motif.
 * `tests` non plus : elle rejoint l'étape traitements. `reponse` non plus : la réponse du patient
 * se lit sous chaque question de l'étape suivi.
 */

export const CONSULTATION_SECTIONS = [
  'motif',
  'bilan-pediatrique',
  'examen',
  'traitement',
  'conclusion',
  'documents',
  'suivi',
  'facturation',
] as const;

export type ConsultationSection = (typeof CONSULTATION_SECTIONS)[number];

export const DEFAULT_SECTION: ConsultationSection = 'motif';

/**
 * Anciennes adresses encore ouvertes. `tests` rejoint l'étape traitements.
 * `reponse` rejoint le suivi : la réponse se lit sous la question, plus sur une étape à part.
 */
const LEGACY_SECTION_TARGETS: Readonly<Record<string, ConsultationSection>> = {
  tests: 'traitement',
  reponse: 'suivi',
};

/** Cible d'une adresse qui n'est plus une étape, ou `null` si elle n'en est pas une. */
export function legacyConsultationSection(value: string | undefined): ConsultationSection | null {
  if (value === undefined) return null;
  return LEGACY_SECTION_TARGETS[value] ?? null;
}

/** Chemin de redirection d'une ancienne adresse, en conservant `?mode=`. */
export function legacyConsultationPath(
  encounterId: string,
  section: string | undefined,
  search: string,
): string | null {
  const target = legacyConsultationSection(section);
  if (target === null) return null;
  const base = `/consultations/${encounterId}/${target}`;
  const query = search.startsWith('?') ? search.slice(1) : search;
  return query.length > 0 ? `${base}?${query}` : base;
}

/** Ce dont dépend l'apparition d'une section. Calculé par la page, jamais par la coque. */
export interface ConsultationSectionContext {
  /** Moins de douze mois à la date de la consultation, ou un bilan déjà saisi. */
  readonly pediatric: boolean;
}

export interface ConsultationSectionSpec {
  readonly id: ConsultationSection;
  readonly icon: LucideIcon;
  /** Clé du libellé de navigation, espace `consultations`. */
  readonly labelKey: string;
  /** Un séparateur est posé au-dessus, comme en V1 avant les sections de fin de séance. */
  readonly separatorBefore?: boolean;
  /** Absente : la section est toujours là. */
  readonly visible?: (context: ConsultationSectionContext) => boolean;
}

export const CONSULTATION_SECTION_SPECS: readonly ConsultationSectionSpec[] = [
  { id: 'motif', icon: ClipboardList, labelKey: 'consultations:sections.motif' },
  {
    id: 'bilan-pediatrique',
    icon: Baby,
    labelKey: 'consultations:sections.bilan_pediatrique',
    visible: (context) => context.pediatric,
  },
  { id: 'examen', icon: Stethoscope, labelKey: 'consultations:sections.examen' },
  {
    id: 'traitement',
    icon: FileText,
    labelKey: 'consultations:sections.traitement',
    separatorBefore: true,
  },
  { id: 'conclusion', icon: FileText, labelKey: 'consultations:sections.conclusion' },
  { id: 'documents', icon: Paperclip, labelKey: 'consultations:sections.documents' },
  {
    id: 'suivi',
    icon: MessageSquareHeart,
    labelKey: 'consultations:sections.suivi',
    separatorBefore: true,
  },
  {
    id: 'facturation',
    icon: Receipt,
    labelKey: 'consultations:sections.facturation',
    separatorBefore: true,
  },
];

export function isConsultationSection(value: string | undefined): value is ConsultationSection {
  return value !== undefined && (CONSULTATION_SECTIONS as readonly string[]).includes(value);
}

/** Les sections qui existent pour cette consultation, dans l'ordre de la navigation. */
export function visibleSections(
  context: ConsultationSectionContext,
): readonly ConsultationSection[] {
  return CONSULTATION_SECTION_SPECS.filter((spec) => spec.visible?.(context) ?? true).map(
    (spec) => spec.id,
  );
}

export function sectionIndex(
  section: ConsultationSection,
  sections: readonly ConsultationSection[],
): number {
  return sections.indexOf(section);
}

export function previousSection(
  section: ConsultationSection,
  sections: readonly ConsultationSection[],
): ConsultationSection | null {
  return sections[sectionIndex(section, sections) - 1] ?? null;
}

export function nextSection(
  section: ConsultationSection,
  sections: readonly ConsultationSection[],
): ConsultationSection | null {
  const index = sectionIndex(section, sections);
  return index < 0 ? null : (sections[index + 1] ?? null);
}

/** Progression affichée dans la barre : la section courante compte comme atteinte. */
export function sectionProgress(
  section: ConsultationSection,
  sections: readonly ConsultationSection[],
): number {
  return Math.round(((sectionIndex(section, sections) + 1) / sections.length) * 100);
}

export type ConsultationMode = 'edit' | 'view';

export function consultationPath(
  encounterId: string,
  section: ConsultationSection,
  mode: ConsultationMode,
  statusMode: ConsultationMode,
): string {
  const base = `/consultations/${encounterId}/${section}`;
  // Le mode déduit du statut n'a pas à figurer dans l'URL : seul l'écart s'écrit.
  return mode === statusMode ? base : `${base}?mode=${mode}`;
}
